/**
 * RequestInfoLinkBuilderPage.tsx
 *
 * Route: /marketing/templates/links/:id — `new` for a new link, else the link id.
 *
 * Its own page, like the email and SMS template builders, so the editor gets the
 * whole height of the screen; the back arrow returns to the Request-Info Links
 * tab. The link comes from the tab's list query (already cached when arriving
 * from the list), so there is no single-link read to keep in step with it.
 */
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { Button, Spinner } from '@/components/ui';
import { REQUEST_INFO_PERSONALIZED_EXTRA_CHARS } from '@/components/sms-editor/sms-editor-constants';
import { getErrorMessage } from '@/services/apiClient';
import * as marketingSvc from '@/services/marketing.service';
import type { RequestInfoLink, RequestInfoLinksResponse } from '@/services/marketing.service';
import RequestInfoLinkEditor from './RequestInfoLinkEditor';
import {
  REQUEST_INFO_LINKS_QUERY_KEY,
  REQUEST_INFO_LINKS_TAB,
  requestInfoLinkBuilderPath,
} from './requestInfoLinkResources';

/** Set by the navigation that follows a create, so the editor says "Link ready". */
interface BuilderLocationState {
  justCreated?: boolean;
  extraChars?: number;
  /** The link just created, until the list reload includes it. */
  link?: RequestInfoLink;
}

export default function RequestInfoLinkBuilderPage() {
  const { id = 'new' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const isNew = id === 'new';
  const state = (location.state ?? null) as BuilderLocationState | null;

  const linksQuery = useQuery({
    queryKey: REQUEST_INFO_LINKS_QUERY_KEY,
    queryFn: () => marketingSvc.listRequestInfoLinks(),
  });

  const backToList = () => navigate(REQUEST_INFO_LINKS_TAB);

  if (linksQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  // A link generated a moment ago can be missing from a reload that raced the
  // write; the navigation carried it, so fall back to that copy.
  const link = isNew
    ? undefined
    : linksQuery.data?.links.find((l) => String(l.id) === id)
      ?? (state?.link && String(state.link.id) === id ? state.link : undefined);

  if (linksQuery.isError || (!isNew && !link)) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-sm text-slate-600">
          {linksQuery.isError
            ? getErrorMessage(linksQuery.error, 'Could not load request-info links.')
            : 'That link was not found.'}
        </p>
        <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />} onClick={backToList}>
          Back to links
        </Button>
      </div>
    );
  }

  return (
    <RequestInfoLinkEditor
      // A fresh editor per link, so moving from "new" to the created link never
      // carries the draft's state over.
      key={isNew ? 'new' : `edit-${id}`}
      link={link}
      justCreated={!isNew && !!state?.justCreated}
      branding={linksQuery.data?.branding}
      extraChars={state?.extraChars ?? linksQuery.data?.personalizedExtraChars ?? REQUEST_INFO_PERSONALIZED_EXTRA_CHARS}
      onBack={backToList}
      onCreated={(created, extraChars) => {
        // Into the cache first, so the page finds it before the reload lands.
        qc.setQueryData<RequestInfoLinksResponse>(REQUEST_INFO_LINKS_QUERY_KEY, (old) =>
          old ? { ...old, links: [created, ...old.links.filter((l) => l.id !== created.id)] } : old,
        );
        void qc.invalidateQueries({ queryKey: REQUEST_INFO_LINKS_QUERY_KEY, exact: true });
        navigate(requestInfoLinkBuilderPath(created.id), {
          replace: true,
          state: { justCreated: true, extraChars, link: created } satisfies BuilderLocationState,
        });
      }}
      onSaved={(saved: RequestInfoLink) => {
        // An edit cannot move lead counts, so patch the row rather than reload the list.
        qc.setQueryData<RequestInfoLinksResponse>(REQUEST_INFO_LINKS_QUERY_KEY, (old) =>
          old ? { ...old, links: old.links.map((l) => (l.id === saved.id ? { ...l, ...saved } : l)) } : old,
        );
        backToList();
      }}
    />
  );
}
